import React from "react";
import { Modal } from "taltech-styleguide";
import { ModalProps } from "./modal.types";
import { ActionButtonProps } from "../buttons";
import { ActionButton } from "../buttons/ActionButton";

/**
 * ConfirmationModal Component
 *
 * This component provides a modal with a header, body, and footer.
 * It can be used to confirm actions or display important information.
 *
 * Props:
 * @param {string} title - The title of the modal.
 * @param {any[]} children - The content to be displayed in the body of the modal.
 * @param {ActionButtonProps[]} actions - The actions to be displayed in the footer of the modal.
 * @param {any} [data] - Optional data to be passed to action buttons.
 * @param {(data?: any) => void} onClose - Function to be called when the modal is closed.
 */
export class ConfirmationModal extends React.Component<ModalProps> {
  /**
   * Header Component
   *
   * This static component renders the header of the modal.
   *
   * Props:
   * @param {string} title - The title to be displayed in the header.
   *
   * Returns:
   * @returns {JSX.Element} The modal header element.
   */
  static Header = ({ title }: { title: string }) => (
    <Modal.Header>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
  );

  /**
   * Body Component
   *
   * This static component renders the body of the modal.
   *
   * Props:
   * @param {any[]} children - The content to be displayed in the body.
   *
   * Returns:
   * @returns {JSX.Element} The modal body element.
   */
  static Body = ({ children }: { children: any[] }) => (
    <Modal.Body>{children}</Modal.Body>
  );

  /**
   * Footer Component
   *
   * This static component renders the footer of the modal.
   *
   * Props:
   * @param {ActionButtonProps[]} actions - The actions to be displayed in the footer.
   * @param {any} [data] - Optional data to be passed to action buttons.
   * @param {(data?: any) => void} onClose - Function to be called when the modal is closed.
   *
   * Returns:
   * @returns {JSX.Element} The modal footer element.
   */
  static Footer = ({
    actions,
    data,
    onClose,
  }: {
    actions: ActionButtonProps[];
    data?: any;
    onClose: (data?: any) => void;
  }) => (
    <Modal.Footer>
      {actions.map((action: any, index) => (
        <ActionButton
          key={index}
          {...action}
          onAction={() => {
            if (action.action === "cancel") onClose();
            else action.onClick({ ...data, onClose: onClose });
          }}
        />
      ))}
    </Modal.Footer>
  );

  /**
   * Render method
   *
   * Renders the ConfirmationModal component with header, body, and footer.
   *
   * Returns:
   * @returns {JSX.Element} The complete modal element.
   */
  render() {
    const componentProps = this.props;
    const { onClose, title, children, actions, data } = componentProps;

    return (
      <>
        <ConfirmationModal.Header title={title} />
        <ConfirmationModal.Body>{children}</ConfirmationModal.Body>
        <ConfirmationModal.Footer
          actions={actions}
          data={data}
          onClose={onClose}
        />
      </>
    );
  }
}
